/* eslint-disable quotes */
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Spinner from "../../Components/Spinner/Spinner";
import { ExportFormat, isType } from "../../shared";
import {
  Admin,
  AdminReport,
  AdminStudent,
  REPORT_TYPES,
} from "../../shared";

import "./TestResultsStudent.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPrint, faSignOutAlt } from "@fortawesome/free-solid-svg-icons";
import { selectControls } from "../../redux/controls";
import { ExitTestResponse } from "./models";
import { useNavigate, useSearchParams } from "react-router-dom";
import { fetchAdminAndStudent } from "./models/TestResultsStudent.utils";
import StudentItem from "../../Components/StudentItem/StudentItem";
import StudentItemDetailDigital from "../../Components/StudentItemDetail/StudentItemDetail";
import { ExportModal } from "../../Components/Modals/ExportModal/ExportModal";
import Modal from '../../Components/Modals/Modal';


const DIGITAL_EXIT_QUERY_TEST_TICKET = "TestTicketID";
const DIGITAL_EXIT_QUERY_REQ_LOGIN = "RequireLogin";
const DIGITAL_EXIT_FROM_QUIZ = "FromQuiz";
const DIGITAL_EXIT_FROM_DEEPLINK = "FromDeepLink";
// const staticMenuStyle = {
//   width: "100%",
// };

export const TestResultsStudent = (): JSX.Element => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const controls = useSelector(selectControls);
  const [exitResponse, setExitResponse] =
    useState<ExitTestResponse | null>(null);
  const [selectedAssessment, setSelectedAssessment] = useState<Admin | null>(
    null
  );
  const [selectedReport, setSelectedReport] = useState<AdminReport | null>(
    null
  );
  const [selectedStudent, setSelectedStudent] = useState<AdminStudent | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [reportOnScreen, setReportOnScreen] = useState<boolean>(false);
  const [requireLogin, setRequireLogin] = useState<boolean>(false);
  const [printSignal, setPrintSignal] = useState<boolean>(false);
  const [exportOpen, setExportOpen] = useState<boolean>(false);
  const [exportSignal, setExportSignal] = useState<ExportFormat>('');
  const [fromQuiz, setFromQuiz] = useState<boolean>(false);
  const [fromDeepLink, setFromDeepLink] = useState<boolean>(false);
  const [origUrl, setOrigUrl] = useState<string>("");

  const [thankYouShowResultsDialogIsOpen, thankYouSetShowResultsDialogOpen] = useState<boolean>(false);

  const getCaseInsensitiveParam = useCallback(
    (paramName: string) : null | string => {
      const keyName = Array.from(searchParams.keys()).find(
        (key) => key.toLowerCase() === paramName.toLowerCase()
      );
      if (!keyName) return null;
      return searchParams.get(keyName);
    }, [searchParams]);

  const printHandler = useCallback(() => {
    setPrintSignal(true);
    setTimeout(() => {
      setPrintSignal(false);
    }, 0);
  }, [setPrintSignal]);

  const exportHandler = useCallback((result, format) => {
    if (!result) {
      setExportOpen(false);
      setExportSignal('');
      return;
    }
    setExportSignal(format);
    setTimeout(() => {
      setExportSignal('');
      setExportOpen(false);
    }, 0);
  }, [setExportSignal]);

  const openExportDialog = useCallback(() => {
    setExportOpen(true);
  }, [setExportOpen]);

  const showResultsHandler = useCallback(() => {
    // if student is not allowed to see results, close thank you modal and redirect to homepage
    thankYouSetShowResultsDialogOpen(false);
    navigate('/home');

  }, [thankYouSetShowResultsDialogOpen, navigate]);



  const returnReportComponent = useCallback(
    (
      admin: Admin | null,
      report: AdminReport | null,
      students: AdminStudent[],
      setReportOnScreen: React.Dispatch<React.SetStateAction<boolean>>
    ): JSX.Element => {
      if (!admin) return <></>;
      switch (report?.rptType) {
      case REPORT_TYPES.STUDENTITEMDETAIL:
        return (
          <StudentItemDetailDigital
            admin={admin}
            students={students}
            fromQuiz={true}
            setReportOnScreen={setReportOnScreen}
            printSignal={printSignal} exportSignal={exportSignal}
          />
        );
      case REPORT_TYPES.STUDENTITEM:
        return (
          <StudentItem
            admin={admin}
            students={students}
            showReportSelectors={requireLogin}
            setReportOnScreen={setReportOnScreen}
            fromQuiz={(fromQuiz || fromDeepLink)}
            printSignal={printSignal}
            exportSignal={exportSignal}
            origUrl={origUrl}
          />
        );
      default:
        return <></>;
      }
    },
    [requireLogin, fromQuiz, fromDeepLink, printSignal, exportSignal, origUrl]
  );

  /**
   * Set loading spinner
   */
  useEffect(() => {
    setIsLoading(true);
  }, []);

  useEffect(() => {
    if (!isLoading) return;
    console.log("searchParams process: ", Array.from(searchParams.keys()));
    if (Array.from(searchParams.keys()).length) {
      const testTicketID = getCaseInsensitiveParam(
        DIGITAL_EXIT_QUERY_TEST_TICKET,
      );
      const reqLoginParam = getCaseInsensitiveParam(
        DIGITAL_EXIT_QUERY_REQ_LOGIN,
      );
      const fromQuizParam = getCaseInsensitiveParam(
        DIGITAL_EXIT_FROM_QUIZ,
      );
      const fromDeepLinkParam = getCaseInsensitiveParam(
        DIGITAL_EXIT_FROM_DEEPLINK,
      );
      setFromQuiz(fromQuizParam === "true" ? true : false);
      setFromDeepLink(fromDeepLinkParam === "true" ? true : false);
      const fromQuizLocal = fromQuizParam === "true" ? true : false;
      const fromDeepLinkLocal = fromDeepLinkParam === "true" ? true : false;
      setRequireLogin(reqLoginParam === "true" ? true : false);
      if (testTicketID) {
        const processDigitalTestExistResults = async () => {
          const response = await fetchAdminAndStudent(testTicketID, fromQuizLocal, fromDeepLinkLocal);
          if (
            isType<ExitTestResponse>(response, "ExitTestResponse")
          ) {
            const results = response;
            setExitResponse(results);
          } else {
            setIsLoading(false);
            alert("Sorry, there was an error fetching test results");
          }
        };
        processDigitalTestExistResults();
      } else {
        setIsLoading(false);
        alert("No test ticket ID found in URL");
      }
    } else {
      setIsLoading(false);
      // return to home page
      navigate("/");

    }
  }, [searchParams, isLoading, getCaseInsensitiveParam, navigate]);

  useEffect(() => {
    if (!exitResponse) return;
    if (
      exitResponse?.administration &&
      exitResponse?.studentTestTicket && exitResponse?.showResults
    ) {
      setSelectedAssessment(exitResponse.administration);
      setSelectedStudent(exitResponse.studentTestTicket);
      const report =
      exitResponse.reports.find(
        (r) => r.rptType === REPORT_TYPES.STUDENTITEMDETAIL
      ) || null;
      setSelectedReport(report);
      setIsLoading(false);
      setSearchParams({});
    } else {
      // if showResults is false, show thank you modal
      setIsLoading(false);
      thankYouSetShowResultsDialogOpen(true);
    }
  }, [exitResponse, setSearchParams, requireLogin, searchParams, thankYouSetShowResultsDialogOpen]);

  useEffect(() => {
    setOrigUrl(window.location.href);
  }, []);

  return (
    <main className="nqt-test-results">
      <Modal
        headerText=''
        bodyText={controls?.ThankYou?.controlText}
        open={thankYouShowResultsDialogIsOpen}
        onClose={() => showResultsHandler()}
        windowType="DESC" />
      <ExportModal open={exportOpen} onClose={(result, format) => exportHandler(result, format)}  disableExcel={selectedReport?.rptType === REPORT_TYPES.STUDENTITEMDETAIL} />
      <section className="d-flex justify-content-end nqt-tr--header">
        <div className={`nqt-tr--print-export-container ${isLoading && 'nqt-tr-loading'}`}>
          <button
            className="nqt-print-btn nqt-clear-btn"
            onClick={printHandler}
            disabled={!reportOnScreen}
          >
            <span className="nqt-print-label">
              {controls?.Rpt_Print?.controlText}
            </span>
            <FontAwesomeIcon
              className="nqt-print-icon fa-signout-alt"
              icon={faPrint}
            />
          </button>
          <button className="nqt-export-btn nqt-clear-btn"
            onClick={() => openExportDialog()}
            disabled={!reportOnScreen}>
            <span className="nqt-export-label">
              {controls?.Rpt_Export?.controlText}
            </span>
            <FontAwesomeIcon
              className="nqt-export-icon fa-signout-alt"
              icon={faSignOutAlt}
            />
          </button>
        </div>
      </section>

      {requireLogin &&
        <section className="nqt-tr--selectors-container">
          <div className="nqt-tr--selector-container">
            <h3 className="nqt-tr--selector-header">
              {controls?.Rpt_SelectReport?.controlText}
            </h3>
          </div>
        </section>
      }
      {!isLoading && (
        <div className="nqt-tr--results" id="report-print-anchor">
          {returnReportComponent(
            selectedAssessment,
            selectedReport,
            [selectedStudent] as AdminStudent[],
            setReportOnScreen
          )}
        </div>
      )}
      <div className="nqt-tr-spinner-container d-flex justify-content-center align-items-center">
        { isLoading &&  <Spinner size={120} containerClassName="nqt-tr-spinner-container" />}
      </div>
    </main>
  );
};

export default TestResultsStudent;
